import {createContext, ReactNode, useContext} from 'react';

export type EditorRole = 'template' | 'document';

interface RoleContextValue {
  role: EditorRole;
  isEditable: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  role: 'document',
  isEditable: false,
});

interface RoleProviderProps {
  children: ReactNode;
  role: EditorRole;
  isEditable?: boolean;
}

export const RoleProvider = ({children, role, isEditable = false}: RoleProviderProps) => {
  return <RoleContext.Provider value={{role, isEditable}}>{children}</RoleContext.Provider>;
};

export function useEditorRole(): RoleContextValue {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useEditorRole must be used within a RoleProvider');
  }
  return context;
}

/**
 * Hook to check if the current editor role allows editing
 */
export function useCanEdit(): boolean {
  const {role, isEditable} = useEditorRole();
  return role === 'template' && isEditable;
}

/**
 * Hook to check if the current editor role is template mode
 */
export function useIsTemplateMode(): boolean {
  const {role} = useEditorRole();
  return role === 'template';
}

/**
 * Hook to check if the current editor role is document mode
 */
export function useIsDocumentMode(): boolean {
  const {role} = useEditorRole();
  return role === 'document';
}
